/**
 * The status lines the Nexus Dex HUD prints while parked on a species detail
 * page or an individual caught-Pokémon page. ONE list, used twice:
 *
 *   - useDexLiveScan pushes them to the native card (iOS NexPip3D, Android
 *     NexPip3D.java), held stable so the copy never flickers at frame rate;
 *   - myPokemonOcr's dropHudEcho recognises the card BY these lines. The
 *     broadcast captures our own card, Vision / ML Kit read it like any other
 *     text, and its "CP 279" / "HP 54" would otherwise be read back as GO's.
 *
 * Android draws no "NEXUS DEX" footer, so on that platform the status line is
 * the only thing that locates the card: every line the HUD can show while a
 * Pokémon page is up MUST be here, and a test holds the two sides together.
 * GO prints none of these.
 */

// ── Species detail page ──────────────────────────────────────────────────────

/** Shared by the detail push and the in-between grid pushes that re-assert it. */
export const DETAIL_HUD_MESSAGE = "Reading species page";
/**
 * A species page that produced NO write: every variant it shows is already
 * recorded. Without it the HUD said "Reading species page" forever on an
 * up-to-date dex, indistinguishable from the scanner being stuck.
 */
export const DETAIL_HUD_UP_TO_DATE_MESSAGE = "Already up to date in Nexus Dex";
/** A species page a Pro scan just WROTE new entries from (2026-07-29 ask). */
export const DETAIL_HUD_UPDATED_MESSAGE = "Nexus Dex updated";
/**
 * The Basic-tier line: Basic SHOWS the detected variant grid as a tease and
 * this line + the CTA say that Pro is what saves them (2026-07-29 v2).
 */
export const DETAIL_HUD_UPSELL_MESSAGE = "Pro saves these to your Dex";
/** The Basic-tier call-to-action under the teased variant grid. */
export const DETAIL_HUD_UPSELL_CTA = "Upgrade now";

// ── Individual caught-Pokémon page (My Pokémon) ──────────────────────────────

export const MYPOKE_HUD_MESSAGE = "Reading your Pokémon";
/** A record was created this dwell. */
export const MYPOKE_HUD_SAVED_MESSAGE = "Saved to My Pokémon";
/** An existing record gained new fields (usually the appraisal IVs). */
export const MYPOKE_HUD_UPDATED_MESSAGE = "My Pokémon updated";
/** The page held nothing new: the record already carries everything read. */
export const MYPOKE_HUD_UP_TO_DATE_MESSAGE = "Already in My Pokémon";
/**
 * The record is saved but the IVs are still unknown, the one field only the
 * appraisal overlay carries. Actionable coaching: the user is one tap away.
 */
export const MYPOKE_HUD_NEED_APPRAISAL_MESSAGE = "Appraise it to add IVs";
/** Saved this dwell, but the IVs are still the appraisal's to give. */
export const MYPOKE_HUD_SAVED_NEED_APPRAISAL_MESSAGE = "Saved. Appraise it to add IVs";
/**
 * The page is recognised but the species is not: a nicknamed Pokémon whose
 * printed name matches no species. The appraisal sheet names the species
 * ("This Slaking was caught…") even when nicknamed, so coach that.
 */
export const MYPOKE_HUD_NEED_SPECIES_MESSAGE = "Appraise it so I can identify it";
/**
 * The storage box is on screen (2026-09-18). ONE status line, no `detail`:
 * the detail slot is the card's big species headline, and the old
 * instruction there drew huge and cut off ("Open a Pokédex page or a").
 */
export const STORAGE_BOX_HUD_MESSAGE = "Storage box. Open a Pokédex or a Pokémon.";

/**
 * Every page-parked status line (the card's TOP row), for the HUD-echo filter
 * and its test. The CTA is not here: it sits under the variant grid, so the
 * filter treats it as an inner line.
 */
export const HUD_PAGE_STATUS_LINES: readonly string[] = [
  DETAIL_HUD_MESSAGE,
  DETAIL_HUD_UP_TO_DATE_MESSAGE,
  DETAIL_HUD_UPDATED_MESSAGE,
  DETAIL_HUD_UPSELL_MESSAGE,
  MYPOKE_HUD_MESSAGE,
  MYPOKE_HUD_SAVED_MESSAGE,
  MYPOKE_HUD_UPDATED_MESSAGE,
  MYPOKE_HUD_UP_TO_DATE_MESSAGE,
  MYPOKE_HUD_NEED_APPRAISAL_MESSAGE,
  MYPOKE_HUD_SAVED_NEED_APPRAISAL_MESSAGE,
  MYPOKE_HUD_NEED_SPECIES_MESSAGE,
  STORAGE_BOX_HUD_MESSAGE,
];
